import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { APIResponse, BasicResponse } from '../_types/api-responses.types';
import {
  Collaborator,
  CustomersPageRequest,
  ExtendedFamilyMember,
  FamilyMember,
  FamilyMemberCreationPayload,
  FamilyMemberCreationRequest,
  FamilyMemberUpdateRequest,
  GetUserAdditionalInfoResponse,
  GetUserCommentsRequest,
  SaveCollaboratorNoteRequest,
  SaveUserCommentRequest,
  User,
  UserDetailsRequest,
} from '../_types/auth.types';
import { CollaboratorNote } from '../_types/collaborator-note.type';
import { UserComment } from '../_types/user-comment.type';
import { CustomerState } from '../enums/customer-state.enum';
import { EntityType } from '../enums/entity-type.enum';
import { Globals } from '../globals';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  _headers: HttpHeaders;

  _baseURL: any;

  user: any;

  deviceId: number;

  deviceInfo: any;

  constructor(
    private httpClient: HttpClient,
    private globaols: Globals,
    private deviceService: DeviceDetectorService
  ) {
    this.deviceId = Date.now();
    this.deviceInfo = this.deviceService.getDeviceInfo();
    const devicedetail: any = {
      platform: 'Website',
      browser: this.deviceInfo.browser,
      browserVersion: this.deviceInfo.browser_version,
      osVersion: this.deviceInfo.os_version,
      deviceId: this.deviceInfo.device + this.deviceInfo.deviceType,
      appVersion: '',
      ipAddress: '',
      macAddress: '',
    };
    this.user = this.globaols.getUser();
    this._headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('User_Agent', JSON.stringify(devicedetail));
    this._baseURL = environment.apiUrl;
  }

  get headers() {
    return this._headers.set('Access-Token', this.globaols.getUser().token);
  }

  _getCustomerList(obj: any): Observable<any> {
    obj.partnerId = this.user.partnerId;
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/user/get/partner/users`,
      obj,
      { headers: this.headers }
    );
  }

  _getAgentCustomerList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/user/get/agent/users`,
      obj,
      { headers: this.headers }
    );
  }

  _getActiveCustomerList(obj: any): Observable<any> {
    obj.partnerId = this.user.partnerId;
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/user/get/partner/active/users`,
      obj,
      { headers: this.headers }
    );
  }

  _getAllActiveAgentList(obj: any): Observable<any> {
    obj.partnerId = this.user.partnerId;
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/partner/get/partner/active/agents`,
      obj,
      { headers: this.headers }
    );
  }

  _assignAgent(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/partner/assign/agent`,
      obj,
      { headers: this.headers }
    );
  }

  _getAllCustomerFamilyList(
    obj: any
  ): Observable<APIResponse<{ familyMemberList: FamilyMember[] }>> {
    return this.httpClient.post<
      APIResponse<{ familyMemberList: FamilyMember[] }>
    >(`${this._baseURL}api/ims/v1/user/get/user/family-member`, obj, {
      headers: this.headers,
    });
  }

  _getCustomerDetails(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/user/details`,
      obj,
      { headers: this.headers }
    );
  }

  _getFamilyMemberDetails(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/user/get/family-member`,
      obj,
      { headers: this.headers }
    );
  }

  _addCustomer(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}api/ims/v1/user/signup`, obj, {
      headers: this.headers,
    });
  }

  _updateCustomer(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}api/ims/v1/user/update`, obj, {
      headers: this.headers,
    });
  }

  changeCustomerState(obj: {
    userId: string;
    userState: CustomerState;
  }): Observable<any> {
    return this.httpClient.put(
      `${this._baseURL}api/ims/v1/partner/update/user/state`,
      obj,
      { headers: this.headers }
    );
  }

  _sendPushNotification(obj: any): Observable<any> {
    const notificationHeaders = new HttpHeaders()
      .set('Access-Token', this.user.token)
      .set('content-type', 'application/json');
    return this.httpClient.post(
      `${this._baseURL}api/communicator/v1/communication/send/notification`,
      obj,
      {
        headers: notificationHeaders,
      }
    );
  }

  getUserDetails(userId: string): Observable<APIResponse<User>> {
    const obj: UserDetailsRequest = {
      entityId: userId,
      entityType: EntityType.USER_ID,
    };
    return this.httpClient.post<APIResponse<User>>(
      `${this._baseURL}api/ims/v1/user/details`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  getUserComments(
    obj: GetUserCommentsRequest
  ): Observable<APIResponse<{ comments: UserComment[] }>> {
    return this.httpClient.post<APIResponse<{ comments: UserComment[] }>>(
      `${this._baseURL}api/ims/v1/partner/comments/get`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  getMemberComments(
    obj: GetUserCommentsRequest
  ): Observable<APIResponse<{ comments: UserComment[] }>> {
    return this.httpClient.post<APIResponse<{ comments: UserComment[] }>>(
      `${this._baseURL}api/ims/v1/partner/comments/get/member`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  saveUserComment(
    obj: SaveUserCommentRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/ims/v1/partner/comments/save`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  getCustomersPage(partnerId: string) {
    return this.httpClient.post<
      APIResponse<{ membersList: ExtendedFamilyMember[] }>
    >(
      `${this._baseURL}api/ims/v1/user/get/partner/non-user-members`,
      { partnerId },
      { headers: this.headers }
    );
  }

  customersPage(
    obj: CustomersPageRequest
  ): Observable<APIResponse<{ membersList: ExtendedFamilyMember[] }>> {
    return this.httpClient.post<
      APIResponse<{ membersList: ExtendedFamilyMember[] }>
    >(`${this._baseURL}api/ims/v1/user/get/partner/customers`, obj, {
      headers: this.headers,
    });
  }

  exportCustomers(obj: CustomersPageRequest): Observable<Blob> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/user/export/customers`,
      obj,
      {
        headers: this.headers,
        responseType: 'blob',
      }
    );
  }

  generateUserPassword(userId: string) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/ims/v1/partner/user/generate-password`,
      { userId },
      { headers: this.headers }
    );
  }

  addFamilyMember(
    obj: FamilyMemberCreationRequest
  ): Observable<APIResponse<FamilyMemberCreationPayload>> {
    return this.httpClient.post<APIResponse<FamilyMemberCreationPayload>>(
      `${this._baseURL}api/ims/v1/user/add/family-member`,
      obj,
      { headers: this.headers }
    );
  }

  updateFamilyMember(
    obj: FamilyMemberUpdateRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}api/ims/v1/user/update/family-member`,
      obj,
      { headers: this.headers }
    );
  }

  getUserAdditionalInfo(userId: string, isMember: boolean = false) {
    return this.httpClient.post<APIResponse<GetUserAdditionalInfoResponse>>(
      `${this._baseURL}api/ims/v1/user/get/additional-info${isMember ? '/member' : ''}`,
      { userId },
      { headers: this.headers }
    );
  }

  updateUserAdditionalInfo(
    userId: string,
    additionalInfo: string,
    isMember: boolean = false
  ) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/ims/v1/user/set/additional-info${isMember ? '/member' : ''}`,
      { userId, additionalInfo },
      { headers: this.headers }
    );
  }

  // Collaborator CRUD methods
  addCollaborator(
    obj: any
  ): Observable<APIResponse<{ collaboratorId: string }>> {
    return this.httpClient.post<APIResponse<{ collaboratorId: string }>>(
      `${this._baseURL}api/ims/v1/user/add/collaborator`,
      obj,
      { headers: this.headers }
    );
  }

  getCollaborator(id: string): Observable<APIResponse<Collaborator>> {
    return this.httpClient.post<APIResponse<Collaborator>>(
      `${this._baseURL}api/ims/v1/user/get/collaborator`,
      { id },
      { headers: this.headers }
    );
  }

  getCollaboratorsByUser(
    userId: string
  ): Observable<APIResponse<{ collaborators: Collaborator[] }>> {
    return this.httpClient.post<APIResponse<{ collaborators: Collaborator[] }>>(
      `${this._baseURL}api/ims/v1/user/get/collaborators/user`,
      { userId },
      { headers: this.headers }
    );
  }

  updateCollaborator(obj: any): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}api/ims/v1/user/update/collaborator`,
      obj,
      { headers: this.headers }
    );
  }

  deleteCollaborator(id: string): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/ims/v1/user/delete/collaborator`,
      { id },
      { headers: this.headers }
    );
  }

  getCollaboratorNotes(
    userId: string,
    collaboratorId: string
  ): Observable<APIResponse<{ notes: CollaboratorNote[] }>> {
    return this.httpClient.post<APIResponse<{ notes: CollaboratorNote[] }>>(
      `${this._baseURL}api/ims/v1/partner/notes/get/collaborator`,
      { userId, collaboratorId },
      { headers: this.headers }
    );
  }

  saveCollaboratorNote(
    obj: SaveCollaboratorNoteRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/ims/v1/partner/notes/save`,
      obj,
      {
        headers: this.headers,
      }
    );
  }
}
